import { Component, Input, OnInit } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { AuthService } from '../../auth/auth.service';
import { UserDto } from '../../auth/user.dto';
import { EmailComposerComponent } from '../../shared/email-composer/email-composer.component';
import { ContactDto } from '../../shared/models/contact.dto';
import { FolderStatus, labelsByFolderStatus } from '../../shared/models/enums/folderStatus.enum';
import { MaritalStatus, labelsByMaritalStatus } from '../../shared/models/enums/marital-status.enum';
import { PhoneContactDto } from '../../shared/models/phone-contact.dto';
import { UserAddressDto } from '../../shared/models/user-address.dto';
import { BeneficiariesService } from '../../shared/services/beneficiaries.service';
import { DateService } from '../../shared/services/date.service';
import { SmsComposerComponent } from '../../shared/sms-composer/sms-composer.component';
import { ToastService } from '../../shared/toast/toast.service';
import { Utils } from '../../shared/utils';
import { BeneficiaryDto } from '../dto/beneficiary.dto';
import { ReferentDto } from '../dto/referent.dto';
import { BeneficiarySummaryExportComponent } from './beneficiary-summary-export/beneficiary-summary-export.component';

@Component({
  selector: 'app-beneficiary-summary',
  templateUrl: './beneficiary-summary.component.html',
})
export class BeneficiarySummaryComponent implements OnInit {
  @Input()
  public beneficiary: BeneficiaryDto;

  @Input()
  public page: string = '';

  public get age(): number | null {
    if (this.beneficiary?.birthDate) {
      return moment(this.dateService.getCurrent()).diff(this.beneficiary.birthDate, 'years');
    }
    return null;
  }

  public get currentReferent(): ReferentDto | null {
    if (this.beneficiary.referents) {
      return this.beneficiary.referents.find((referent) => !referent.historized);
    }
    return null;
  }

  public readonly folderStatus: Map<FolderStatus, string> = labelsByFolderStatus;
  public readonly maritalStatus: Map<MaritalStatus, string> = labelsByMaritalStatus;

  private user: UserDto;

  constructor(
    private readonly dateService: DateService,
    private readonly toastService: ToastService,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
    private readonly beneficiariesService: BeneficiariesService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.user = await this.authService.getUser();
  }

  public async openModal(event?: Event): Promise<void> {
    if (event) {
      event.preventDefault();
    }

    if (!this.beneficiary || !Utils.isNotEmpty(this.beneficiary.emailAddress)) return;

    if (this.beneficiary.email) {
      const modal: NgbModalRef = this.modalService.open(EmailComposerComponent, {
        windowClass: 'rightModal',
        size: 'lg',
        backdrop: 'static',
      });

      const modalComponent: EmailComposerComponent = modal.componentInstance as EmailComposerComponent;
      const beneficiaryName: string =
        (Utils.isNotEmpty(this.beneficiary.firstName) ? this.beneficiary.firstName + ' ' : '') + this.beneficiary.usualName;
      return modalComponent.onInit(
        this.beneficiary.id,
        beneficiaryName,
        new ContactDto(beneficiaryName, this.beneficiary.emailAddress),
        this.getSignature(),
      );
    }

    this.toastService.show(
      "Vous ne pouvez pas effectuer cette opération, car le bénéficiaire n'a pas autorisé l'envoi de message électronique.",
      'warning',
    );
  }

  public openSmsModal(event?: Event): void {
    if (event) {
      event.preventDefault();
    }

    if (!this.beneficiary || !Utils.isNotEmpty(this.beneficiary.cellPhoneNumber)) return;

    if (this.beneficiary.sms) {
      const modal: NgbModalRef = this.modalService.open(SmsComposerComponent, {
        windowClass: 'rightModal',
        size: 'lg',
        backdrop: 'static',
      });

      const modalComponent: SmsComposerComponent = modal.componentInstance as SmsComposerComponent;
      const beneficiaryName: string =
        (Utils.isNotEmpty(this.beneficiary.firstName) ? this.beneficiary.firstName + ' ' : '') + this.beneficiary.usualName;
      return modalComponent.onInit(
        this.beneficiary.id,
        beneficiaryName,
        new PhoneContactDto(beneficiaryName, this.beneficiary.cellPhoneNumber),
        this.getSmsSignature(),
      );
    }

    this.toastService.show("Vous ne pouvez pas effectuer cette opération, car le bénéficiaire n'a pas autorisé l'envoi de SMS.", 'warning');
  }

  public openSummaryExportModal(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    if (!this.beneficiary) return;
    const modal: NgbModalRef = this.modalService.open(BeneficiarySummaryExportComponent, {
      windowClass: 'rightModal',
      size: 'lg',
      backdrop: 'static',
    });
    const modalComponent: BeneficiarySummaryExportComponent = modal.componentInstance as BeneficiarySummaryExportComponent;
    return modalComponent.onInit(this.beneficiary.id);
  }

  private getSignature(): string {
    if (this.user) {
      let address: UserAddressDto[] = [];
      if (this.user.postalAddresses && this.user.postalAddresses.length > 0) {
        address = this.user.postalAddresses;
      }
      const position: string = address && address.length > 0 ? address[0].position : undefined;
      const telephone: string = address && address.length > 0 ? address[0].telephone : undefined;
      return (
        '<p><br></p>' +
        '<p><br></p>' +
        '<p><strong>' +
        this.user.name +
        (Utils.isNotEmpty(position) ? '<p><strong style="color: rgb(136, 136, 136);">' + position + '</strong></p>' : '') +
        '</strong></p>' +
        (Utils.isNotEmpty(telephone) ? '<p><strong  style="color: rgb(136, 136, 136);">' + telephone + '</strong></p>' : '') +
        '<p><a href="mailto:' +
        this.user.email +
        '" target="_blank">' +
        this.user.email +
        '</a></p>'
      );
    }
    return null;
  }

  public getSmsSignature() {
    return (
      '\n' +
      this.user.firstname +
      ' ' +
      this.user.lastname +
      (this.user.postalAddresses && this.user.postalAddresses.length > 0 && this.user.postalAddresses[0].telephone
        ? `\n${this.user.postalAddresses[0].telephone}`
        : '')
    );
  }
}
